import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../Services/firebase-config";
import { eventsData } from "../../Components/Events/eventsData";
import { useAuth } from "../../Contexts/AuthContext";
import { convertDeadlineToDate } from "../../Components/helpers/helpers";

const LoginPage = () => {
  const { currentUser, userData, setUserData } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Get event parameters from both URL params and query string
  const isEvent =
    params.isEvent === "true" ||
    new URLSearchParams(location.search).get("isEvent") === "true" ||
    location.state?.isEvent === true;
  const eventId =
    params.eventId ||
    new URLSearchParams(location.search).get("eventId") ||
    new URLSearchParams(location.search).get("id") ||
    location.state?.eventId;

  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser && userData) {
      // User is already logged in, redirect based on role
      if (userData?.role === "Administrator") {
        navigate("/eventdashboard/dashboard");
      } else if (userData?.role === "Judge") {
        navigate("/eventdashboard/manage-applications");
      } else if (userData?.role === "Mentor") {
        navigate("/eventdashboard/teams-management");
      } else if (eventId) {
        // Regular user in event context
        navigate(`/hackathon-dashboard`, {
          state: { isEvent: true, eventId: eventId },
        });
      } else {
        // Regular user, redirect to home or appropriate page
        const from = location.state?.from?.pathname || "/";
        navigate(from);
      }
    }
  }, [currentUser, userData, navigate, isEvent, eventId, location.state]);

  const handleSubmit = async (event) => {
    setError(null);
    setIsLoggingIn(true);
    event.preventDefault();

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      const userDocRef = doc(db, "users", userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      console.log("User data fetched successfully:", userData);
      setUserData(userData);

      // Check user role and redirect accordingly
      if (userData?.role === "Administrator") {
        navigate("/eventdashboard/dashboard");
      } else if (userData?.role === "Judge") {
        navigate("/eventdashboard/manage-applications");
      } else if (userData?.role === "Mentor") {
        navigate("/eventdashboard/teams-management");
      } else if (eventId) {
        // Regular user in event context
        navigate(`/hackathon-dashboard`, {
          state: { isEvent: true, eventId: eventId },
        });
      } else {
        navigate("/my-events");
      }
    } catch (error) {
      console.error("Error logging in: ", error);
      if (error.code === "auth/invalid-credential") {
        setError("Invalid email or password");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Generate the appropriate signup link based on event context
  const handleSignupClick = () => {
    if (isEvent && eventId) {
      navigate(`/register`, {
        state: { isEvent: true, eventId: eventId },
      });
    } else {
      navigate("/register");
    }
  };

  // Check target event deadline and hide signup link if registration closed
  const targetEvent = eventsData.find((e) => e.id === eventId);

  const deadlineTimestamp = convertDeadlineToDate(targetEvent?.deadline);
  const isRegistrationClosed =
    deadlineTimestamp && Date.now() > deadlineTimestamp;

  return (
    <div
      className="h-screen bg-cover bg-center flex flex-col justify-center items-center"
      style={{ backgroundImage: `url('/Images/small2.jpg')` }}
    >
      <div className="max-w-md w-full bg-custom-blue-1 p-8 border shadow">
        <div className="flex justify-center mb-8">
          <img className="w-20" src={"/Images/STIlogo.svg"} alt="STI Logo" />
        </div>
        <h2 className="text-2xl font-normal mb-2 text-white text-center">
          LOGIN
        </h2>
        <h3 className="text-sm font-small mb-10 text-white text-center">
          Welcome back to the STI Events Platform
        </h3>
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-sm text-white font-semibold mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm text-white font-semibold mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              {!isRegistrationClosed && (
                <button
                  type="button"
                  onClick={handleSignupClick}
                  className="text-sm text-white cursor-pointer hover:underline"
                >
                  Don't have an account?{" "}
                  <span className="font-bold">SIGN UP</span>
                </button>
              )}
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate("/forgotpassword")}
                className="font-small text-white hover:text-custom-green-1"
              >
                Forgot Password?
              </button>
            </div>
          </div>
          <div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-custom-green-1 text-custom-blue-1 py-2 px-4 border border-transparent hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
